import Vue from 'vue'
import Vuex from 'vuex'
import VuexUndoRedo from "vuex-undo-redo";

import { UUID } from '../util/prototypes';
import { authStoreModule } from './auth.store';
import { AppService } from './app.service';
import {
    Component, localData,
    Gradient, BoxShadow,
    BorderRadius, Typography,
    Grid
} from './models';

import { createComponentForm } from './forms.config';


Vue.use(Vuex)
Vue.use(VuexUndoRedo);

function getColor(bgs, lab) {
    return bgs.reduce((prev, curr) => {
        const split = curr.split("-");
        const label =
            split[2] && (+split[2] < 1000)
                ? split[1] + "-color"
                : lab;
        if (prev[label]) {
            prev[label].push(curr);
            return { ...prev };
        } else {
            prev[label] = [curr];
            return { ...prev };
        }
    }, {});
}

function getRootState() {
    return {
        rootNode: document.createElement('div'),
        components: [],
        selectedTool: 'rect',
        selectedNodes: [],
        cssStyles: {},
        clipbaord: null,
        tailwind: {},
        isPreview: false,
        styleMode: 'normal',
        screenSize: 'all',
        selectedComponent: null,
        name: '',
        componentSnapshot: {},
        featherIcons: [],
        user: null,
        zoomPercent: 100,
        screenWidth: 1280,
        screenHeight: 1080,
        isSync: false,
        images: [],
        gradients: [],
        shadows: [],
        borderRadiuses: [],
        typographies: [],
        copyValue: 1,
        grids: [],
        forms: { createComponentForm }
    }
}

export const store = new Vuex.Store({
    state: {
        ...getRootState()
    },
    getters: {
        UUID: () => {
            return 'xxxx-xxxxxx-xxxxxx'.replace(/[xy]/g, function (c) {
                var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        },
        selectedTool: (state) => state.selectedTool,
        tools: () => ({ pointer: 'pointer', rect: 'rect', image: "image", input: 'input', button: 'button', bgImage: 'bgImage', video: 'video' }),
        selectedNodes: (state) => state.selectedNodes,
        isSelectedNode: (state) => (item) => state.selectedNodes ? state.selectedNodes.id === item.id : false,
        selectedNodeClassList: (state) => state.selectedNodes ? state.selectedNodes.classListArray : [],
        selectedComponent: state => state.selectedComponent,
        isSelectedComponent: state => (com) => state.selectedComponent ? com.id === state.selectedComponent.id : false,
        cssStyles: (state) => {
            return state.cssStyles;
        },
        components: state => state.components,
        componentsOnChange: state => state.componentSnapshot.docsChange,
        rootNode: state => state.rootNode,
        saveHtml: state => state.rootNode.toSave(),
        clipbaord: state => state.clipbaord,
        tailwind: state => state.tailwind,
        width: state => state.tailwind && state.tailwind['.w'] ? [
            {
                key: 'Fixed',
                value: state.tailwind[".w"].filter((item) => !item.includes('\\'))
            },
            {
                key: '2 Column',
                value: state.tailwind[".w"].filter((item) => item.includes('/2')).map(item => item.replace("\\", "")),
            },
            {
                key: '3 Column',
                value: state.tailwind[".w"].filter((item) => item.includes('/3')).map(item => item.replace("\\", "")),
            },
            {
                key: '4 Column',
                value: state.tailwind[".w"].filter((item) => item.includes('/4')).map(item => item.replace("\\", "")),
            },
            {
                key: '5 Column',
                value: state.tailwind[".w"].filter((item) => item.includes('/5')).map(item => item.replace("\\", "")),
            },
            {
                key: '6 Column',
                value: state.tailwind[".w"].filter((item) => item.includes('/6')).map(item => item.replace("\\", "")),
            },
            {
                key: '12 Column',
                value: state.tailwind[".w"].filter((item) => item.includes('/12')).map(item => item.replace("\\", "")),
            }
        ] : [],
        height: state => state.tailwind && state.tailwind['.h'] ? [
            {
                key: 'Fixed',
                value: state.tailwind[".h"].map(item => item.replace("\\", "")),
            }
        ] : [],

        bg: state => state.tailwind && state.tailwind['.bg'] ? state.tailwind[".bg"].filter(item => item.hasNumber()) : [],
        background: state => state.tailwind && state.tailwind['.bg'] ? [
            ...state.tailwind[".bg"],
        ]
            : [],
        shadow: state => state.tailwind && state.tailwind['.shadow'] ? [...state.tailwind[".shadow"]] : [],
        rounded: state => state.tailwind && state.tailwind['.rounded'] ? [
            {
                key: "All", value: ['rounded', 'rounded-none', 'rounded-sm', 'rounded-md', 'rounded-lg', 'rounded-full'],
            },
            {
                key: "Left", value: state.tailwind[".rounded"].filter((item) => item.includes('-l-')),
            }, {
                key: "Top", value: state.tailwind[".rounded"].filter((item) => item.includes('-t-')),
            }, {
                key: "Right", value: state.tailwind[".rounded"].filter((item) => item.includes('-r-')),
            },
            {
                key: "Bottom", value: state.tailwind[".rounded"].filter((item) => item.includes('-b-')),
            },
            {
                key: "Top-Left", value: state.tailwind[".rounded"].filter((item) => item.includes('-tl-')),
            },
            {
                key: "Top-Right", value: state.tailwind[".rounded"].filter((item) => item.includes('-tr-')),
            },
            {
                key: "Bottom-Left", value: state.tailwind[".rounded"].filter((item) => item.includes('-bl-')),
            },
            {
                key: "Bottom-Right", value: state.tailwind[".rounded"].filter((item) => item.includes('-br-')),
            },
        ] : [],
        text: state => state.tailwind && state.tailwind['.text'] ? {
            text: state.tailwind[".text"] ? state.tailwind[".text"] : [],
            fonts: state.tailwind[".font"],
            fontsFamily: ['font-bellota', 'font-lato', 'font-sen', 'font-roboto', 'font-open-sans', 'font-pt-sans', 'font-playfair']
        } : [],
        flex: state => state.tailwind && state.tailwind['.flex'] ? [
            { key: 'Flex', value: ['flex', ...state.tailwind[".flex"]] },
            { key: 'Justify', value: state.tailwind[".justify"] },
            { key: 'Items', value: state.tailwind[".items"] },
            { key: 'Align', value: state.tailwind[".align"] },
            { key: 'Self', value: state.tailwind[".self"] },
            { key: 'Content', value: state.tailwind[".content"] }
        ] : [],
        border: state => state.tailwind && state.tailwind['.border'] ? [...state.tailwind[".border"]] : [],
        border: state => state.tailwind && state.tailwind['.border'] ? [...state.tailwind[".border"]] : [],
        margin: (state) => {
            try {
                return state.tailwind ? [
                    {
                        key: 'M',
                        value: state.tailwind[".m"]
                    },
                    {
                        key: 'MX',
                        value: state.tailwind[".mx"],
                    },
                    {
                        key: 'MY',
                        value: state.tailwind[".my"],
                    },
                    {
                        key: 'ML',
                        value: state.tailwind[".ml"],
                    },
                    {
                        key: 'MT',
                        value: state.tailwind[".mt"],
                    },
                    {
                        key: 'MR',
                        value: state.tailwind[".mr"],
                    },
                    {
                        key: 'MB',
                        value: state.tailwind[".mb"],
                    },
                    {
                        key: '-M',
                        value: state.tailwind["."].filter((item) => item.includes('-m-'))
                    },
                    {
                        key: '-MX',
                        value: state.tailwind["."].filter((item) => item.includes('-mx')),
                    },
                    {
                        key: '-MY',
                        value: state.tailwind["."].filter((item) => item.includes('-my')),
                    },
                    {
                        key: '-ML',
                        value: state.tailwind["."].filter((item) => item.includes('-ml')),
                    },
                    {
                        key: '-MT',
                        value: state.tailwind["."].filter((item) => item.includes('-mt')),
                    },
                    {
                        key: '-MR',
                        value: state.tailwind["."].filter((item) => item.includes('-mr')),
                    },
                    {
                        key: '-MB',
                        value: state.tailwind["."].filter((item) => item.includes('-mb')),
                    },
                ] : []
            } catch (error) {
                return []
            }
        },
        padding: (state) => {
            return state.tailwind ? [
                {
                    key: 'P',
                    value: state.tailwind[".p"]
                },
                {
                    key: 'PX',
                    value: state.tailwind[".px"],
                },
                {
                    key: 'PY',
                    value: state.tailwind[".py"],
                },
                {
                    key: 'PL',
                    value: state.tailwind[".pl"],
                },
                {
                    key: 'PT',
                    value: state.tailwind[".pt"],
                },
                {
                    key: 'PT',
                    value: state.tailwind[".pr"],
                },
                {
                    key: 'PB',
                    value: state.tailwind[".pb"],
                },
            ] : []
        },
        transition: (state) => {
            return [{
                key: "Transistion",
                value: [
                    {
                        key: 'Transition',
                        value: state.tailwind[".transition"]
                    },
                    {
                        key: 'Duration',
                        value: state.tailwind[".duration"]
                    },
                    {
                        key: 'Ease',
                        value: state.tailwind[".transition"]
                    },
                    {
                        key: 'Transform',
                        value: [
                            ...state.tailwind[".transform"],
                            "transform"
                        ]
                    },
                    {
                        key: 'Scale',
                        value: [
                            ...state.tailwind[".scale"],
                            ...state.tailwind["."].filter((item) => item.includes('-scale'))
                        ]
                    },
                    {
                        key: 'Translate',
                        value: [
                            ...state.tailwind[".translate"].map((item) => item.replace('\\', '')),
                            ...state.tailwind["."].filter((item) => item.includes('-transla')).map((item) => item.replace('\\', '')),
                        ]
                    },
                    {
                        key: 'Rotate',
                        value: [
                            ...state.tailwind[".rotate"],
                            ...state.tailwind["."].filter((item) => item.includes('-rot')),
                        ]
                    },
                    {
                        key: 'Skew',
                        value: [
                            ...state.tailwind[".skew"],
                            ...state.tailwind["."].filter((item) => item.includes('-skew'))
                        ]
                    }
                ]
            }]
        },
        position: (state) => {
            return [
                {
                    key: "Position", value: [
                        'relative', 'absolute', 'sticky', "fixed",
                        ...state.tailwind[".left"],
                        ...state.tailwind[".top"],
                        ...state.tailwind[".right"],
                        ...state.tailwind[".bottom"],
                        ...state.tailwind[".z"],
                        ...state.tailwind[".overflow"]
                    ]
                }
            ]
        },
        opacity: (state) => {
            return [
                { key: "Opacity", value: state.tailwind[".opacity"] },
            ]
        },
        isPreview: state => state.isPreview,
        styleMode: state => state.styleMode,
        screenSize: state => state.screenSize,
        isClassSelected: state => (cls) => {
            if (state.selectedNodes.length) {
                return state.selectedNodes.classListArray.includes(state.styleMode == 'normal' ? cls : state.styleMode + ':' + cls)
                // || state.selectedNodes.classListArray.includes(state.screenSize == 'all' ? cls : state.screenSize + ':' + cls);
            } else {
                return false;
            }
        },
        featherIcons: state => state.featherIcons ? state.featherIcons : [],
        user: state => state.user,
        selectedNodeClassList: state => state.selectedNodes ? state.selectedNodes.classListArray : [],
        selectedNodeParents: state => state.selectedNodes ? state.selectedNodes.parents() : [],
        zoomPercent: state => state.zoomPercent ? state.zoomPercent : 100,
        screenWidth: state => state.screenWidth,
        screenHeight: state => state.screenHeight,
        isSync: state => state.isSync,
        localDataString: state => localStorage.getItem("localData"),
        localData: state => {
            const str = localStorage.getItem("localData");
            const data = str ? JSON.parse(str) : {};
            return data;
        },
        name: state => state.name,
        images: state => state.images,
    },
    mutations: {
        emptyState() {
            this.replaceState(getRootState());
        },
        updateSelectedTool: (state, payload) => {
            state.selectedTool = payload;
        },
        setName(state, payload) {
            state.name = payload;
        },
        setComponents(state, { components }) {
            state.components = components;
        },
        selectComponent(state, { component }) {
            if (component) {
                state.selectedComponent = component ? component : state.selectedComponent;
                state.rootNode.parse(component.code);
                // state.selectedNodes = null;
                // state.selectedNodes = state.rootNode;
                try {
                    state.selectedNodes.classList.add("outline-select");
                } catch (error) {
                    console.error("Error in Store:selectComponent(): ", error);
                }
            }
        },
        setSelectedNode: (state, { isMulti, node }) => {
            if (isMulti) {
                state.selectedNodes = [...state.selectedNodes, node];
            } else {
                state.selectedNodes = [node];
            }
            try {
                state.selectedNodes.classList.add("outline-select");
                state.typedText = state.selectedNodes.innerText
            } catch (error) {
                console.error("Error in Store:setSelectedNode(): ", error);
            }
        },
        deSelectAll(state, isParent = true) {
            const all = Array.from(state.rootNode.querySelectorAll(".outline-select"));
            all.push(state.rootNode);
            for (let key in all) {
                const el = all[key].classList;
                el.remove("outline-select");
                el.add("outline");
            }
        },
        deleteNode(state, payload) {
            payload.deleteNode();
        },
        setStyles(state, payload) {
            state.cssStyles = { ...payload }
        },
        setTailwind(state, payload) {
            state.tailwind = { ...payload }
        },
        addClass(state, payload) {
            if (state.rootNode !== state.selectedNodes) {
                let cls = payload;
                if (state.styleMode !== 'normal') {
                    cls = state.styleMode + ':' + payload;
                } else if (state.screenSize !== 'all') {
                    cls = state.screenSize + ':' + payload;
                }
                const items = state.selectedNodes;
                const list = items.classListArray;
                if (list.includes(cls)) {
                    items.classList.remove(cls);
                } else {
                    items.classList.add(cls);
                }
                state.selectedNodes = [...items];
            }
        },
        setRootNode(state, payload) {
            state.rootNode = payload;
        },
        setStyleMode(state, payload) {
            state.styleMode = payload;
        },
        setScreenSize(state, payload) {
            state.screenSize = payload;
            /* Small (sm) */
            // @media(min - width: 640px) {  }
            /* Medium (md) */
            // @media(min - width: 768px) {  }
            /* Large (lg) */
            // @media(min - width: 1024px) {  }
            /* Extra Large (xl) */
            // @media(min - width: 1280px) {  }
            if (payload === 'sm') {
                state.screenWidth = 640;
            } else if (payload === 'md') {
                state.screenWidth = 768;
            } else if (payload === 'lg') {
                state.screenWidth = 1024;
            } else if (payload === 'xl') {
                state.screenWidth = 1280;
            } else {
                state.screenWidth = 1440;
            }
        },
        addNode(state, payload) {
            let item = null;
            if (!payload) {
                const tool = state.selectedTool.tool;
                if (tool === "rect") {
                    item = document.createElement(state.selectedTool.value.toLowerCase());
                    item.setAttribute("class", "outline p-1 min");
                } else if (tool === "image") {
                    item = document.createElement("img");
                    item.setAttribute("class", "outline h-16");
                    item.setAttribute("src", "https://source.unsplash.com/random");
                } else if (tool === "video") {
                    item = document.createElement("video");
                    item.setAttribute("class", "outline h-16");
                    item.setAttribute(
                        "src",
                        "https://interactive-examples.mdn.mozilla.net/media/examples/flower.webm"
                    );
                    item.setAttribute("controls", "");
                } else if (tool === "input") {
                    item = document.createElement("input");
                    item.setAttribute("class", "border text-sm p-1 rounded w-32 h-10");
                    item.setAttribute("placeholder", "Untitiled");
                } else if (tool === "button") {
                    item = document.createElement("button");
                    item.setAttribute("class", "border text-sm p-1 rounded w-32 h-10");
                    item.setAttribute("type", "button");
                    item.innerHTML = "Submit";
                } else if (tool === 'lorem') {
                    state.selectedNodes.innerText = "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum."
                } else if (tool == 'grid') {
                    const grid = state.selectedTool.value;
                    item = document.createElement('div');
                    item.setAttribute('class', `outline p-1 min ${grid.name}`);
                    grid.items.forEach((gItem) => {
                        const div = document.createElement('div');
                        div.setAttribute('class', `outline p-1 min ${gItem.name}`);
                        div.setAttribute("id", UUID());
                        div.setAttribute("name", gItem.name);
                        item.appendChild(div);
                    })
                }
                if (item) {
                    item.setAttribute("id", UUID());
                    state.selectedNodes.appendChild(item);
                }
            } else {

            }
        },
        setSize(state, { percent, width, height }) {
            state.zoomPercent = +percent;
            state.screenWidth = width;
            state.screenHeight = height;
        },
        copy(state) {
            state.clipbaord = null;
            state.clipbaord = state.selectedNodes;
        },
        paste(state) {
            for (let i = 0; i < state.copyValue; i++) {
                const clone = state.clipbaord.cloneNode(true);
                clone.setAttribute('id', UUID());
                state.selectedNodes.appendChild(clone);
            }
        },
        setPreview(state, payload) {
            state.isPreview = payload;
        },
        setComponentSnapshot(state, payload) {
            state.componentSnapshot = payload;
        },
        setUser(state, user) {
            state.user = user;
            localData.setUser(user);
            // localStorage.setItem('authUser', JSON.stringify(user));
        },
        createComponentLocal(state, payload) {

        },
        updateComponentLocal(state, { component, isSync }) {
            if (state.user) {
                state.isSync = isSync;
                state.selectedComponent.code = document.querySelector('#wrapper').toSave();;
                state.selectedComponent.name = state.name.trim() ? state.name : state.selectedComponent.name;
                const find = state.components.find((item) => item.doc.id === state.selectedComponent.doc.id);
                find.code = state.selectedComponent.code;
                find.name = state.selectedComponent.name;
                localData.setItem("isSync", isSync);
            }
        },
        setImages(state, { images }) {
            state.images = images;
        },
        setGradients(state, payload) {
            state.gradients = payload;
        }
    },
    actions: {
        addNode: ({ commit, dispatch }) => {
            commit('addNode');
        },
        updateComponentLocal({ commit, dispatch, getters }, isSync) {
            const code = document.querySelector('#wrapper').toSave();
            if (isSync) {
                if (getters.selectedComponent) {
                    commit('updateComponentLocal', { isSync });
                } else {
                    AppService.addComponent({ code }).then((resp) => {
                        const component = { docId: resp.id, attrs: { code } };
                        commit("updateComponentLocal", { component, isSync });
                    });
                }
            } else {
                commit('updateComponentLocal', { isSync });
            }
        },
        sync({ state, getters, dispatch }) {
            dispatch('updateComponentLocal', true);
            const promiseAll = Promise.all(state.components.map((item) => item.update()));
            promiseAll.then((resp) => {
                dispatch('updateComponentLocal', false);
            });
        },
        selectTool: ({ commit, getters, dispatch }, { tool, value }) => {
            commit('updateSelectedTool', { tool, value: value });
            commit('deSelectAll', false);
            getters.selectedNodes.classList.add('outline-select');
            commit('addNode');
            dispatch('updateComponentLocal', true);
        },
        addIcon({ commit, getters, state, dispatch }, { icon }) {
            return new Promise((resolve, reject) => {
                commit('updateSelectedTool', { tool: 'icon', value: icon });
                commit('deSelectAll', false);
                getters.selectedNodes.classList.add('outline-select');
                const item = document.createElement('i');
                let name = state.selectedTool.value;
                item.setAttribute('data-feather', name);
                commit('updateSelectedTool', { tool: 'icon', value: null });
                commit('deSelectAll', false);
                getters.selectedNodes.classList.add('outline-select');
                state.selectedNodes.appendChild(item);
                dispatch('updateComponentLocal', true);
                resolve(item);
            })
        },
        applyImage({ state }, payload) {
            if (state.selectedNodes.nodeName == 'IMG') {
                state.selectedNodes.setAttribute('src', payload.regular);
            } else {
                state.selectedNodes.setBackgroundImage(`url("${payload.regular}")`);
            }
        },
        addClass({ commit, getters, dispatch }, payload) {
            commit('addClass', payload);
            dispatch('updateComponentLocal', true);
        },
        deleteNode({ commit, dispatch }, payload) {
            commit('deleteNode', payload)
            dispatch('updateComponentLocal', true);
        },
        selectNode({ commit }, payload) {
            commit('deSelectAll');
            commit('setSelectedNode', payload);
        },
        selectParent({ dispatch, state }, { children }) {
            state.selectedNodes = [];
            children.forEach((item) => {
                dispatch('selectNode', { isMulti: true, node: item.parentNode });
            })
        },
        selectChildren({ dispatch, state }, { parentNode }) {
            if (parentNode) {
                state.selectedNodes = [];
                parentNode.forEach((node) => {
                    node.children.forEach((item) => {
                        dispatch('selectNode', { isMulti: true, node: item });
                    });
                })
            }
        },
        setStyles({ commit }, payload) {
            commit('setStyles', payload)
        },
        setTailwind({ commit }, payload) {
            commit('setTailwind', payload)
        },
        setRootNode({ commit }, payload) {
            commit('setRootNode', payload);
        },
        copy({ commit }) {
            commit('copy');
        },
        paste({ commit }) {
            commit('paste')
        },
        setSize({ commit }, payload) {
            commit("setSize", payload)
        },
        preview({ commit, getters }) {
            commit('setPreview', !getters.isPreview);
            if (getters.isPreview) {
                let all = getters.rootNode
                    .querySelectorAll(".outline,.outline-select")
                    .list();
                all.push(getters.rootNode);
                for (let i in all) {
                    all[i].classList.remove("outline");
                    all[i].classList.remove("outline-select");
                }
            } else {
                const all = getters.rootNode.querySelectorAll("div,main").list();
                all.push(getters.rootNode);
                for (let i in all) {
                    all[i].classList.add("outline");
                    getters.selectedNodes.classList.add("outline-select");
                }
            }
        },
        setStyleMode({ commit }, payload) {
            commit('setStyleMode', payload)
        },
        setScreenSize({ commit }, payload) {
            commit('setScreenSize', payload)
        },
        createNewComponent({ commit, getters }, { name, code }) {
            return new Promise(async (resolve, reject) => {
                try {
                    const el = document.createElement("div");
                    el.setAttribute("class", "h-full bg-gray-900 p-2");
                    new Component({
                        code: code ? code.toSave(true) : el.toSave(),
                        name: name
                    }).create().then(({ component }) => {
                        resolve({ component });
                        commit('selectComponent', { component });
                    });
                } catch (error) {
                    console.error("SyncStore: addComponent", error);
                    reject(error);
                }
            })
        },
        deleteComponent({ commit, getters }, payload) {
            AppService.deleteComponent({ doc: payload });
        },
        onRename({ commit, state }, payload) {
            AppService.updateComponent({ docId: state.selectedComponent.id, attrs: { name: payload } });
        },
        async fetchComponents({ commit, getters, dispatch }) {
            Component.list((components) => {
                commit('setComponents', { components });
                commit('selectComponent', { component: components.length && !getters.selectedComponent ? components[0] : getters.selectedComponent })
            });
        },
        selectComponent({ commit }, { component }) {
            commit('selectComponent', { component });
        },
        editTemplate({ commit, getters }, payload) {
            const { user } = localData.getUser();
            try {
                AppService.addComponent({
                    code: payload.code,
                    name: payload.name,
                    uid: user.uid,
                    user: {
                        displayName: user.displayName,
                        photoURL: user.photoURL,
                        email: user.email
                    }
                });
            } catch (error) {
                console.error("SyncStore: editTemplate", error);
            }
        },

        // ============================ AUTH ACTIONS ========================== //
        loadUser({ commit }) {
            const user = localData.getUser();
            commit('setUser', user);
        },
        async login({ commit }, { email, password }) {
            try {
                const resp = await firebase.auth().signInWithEmailAndPassword(email, password);
                commit('setUser', resp);
                return resp;
            } catch (error) {
                console.error("Error:Login", error);
                throw error;
            }
        },
        async loginWithGoogle({ commit, dispatch }) {
            try {
                const resp = await AppService.loginWithGoogle();
                commit('setUser', resp);
                dispatch('fetchComponents')
            } catch (error) {
                console.error("Error in authStoreModule:loginWithGoogle", error)
            }
        },
        setName({ commit, dispatch }, payload) {
            commit('setName', payload);
            dispatch('updateComponentLocal', true);
        },
        setImages({ commit }, { images }) {
            commit('setImages', { images })
        },
        loadAllData({ dispatch }) {
            dispatch('loadGradients');
            dispatch('loadBoxShadows');
            dispatch('loadBorderRadius');
            dispatch('loadGrids');
        },
        loadGradients({ state }) {
            Gradient.loadGradientsToDom((payload) => {
                state.gradients = payload;
            });
        },
        loadBoxShadows({ state }) {
            BoxShadow.loadBoxShadowsToDom((payload) => {
                state.shadows = payload;
            });
        },
        loadBorderRadius({ state }) {
            BorderRadius.loadBorderRadiusToDom((payload) => {
                state.borderRadiuses = payload;
            });
        },
        loadTypography({ state }) {
            Typography.loadTypographyToDom((payload) => {
                state.typographies = payload;
            });
        },
        loadGrids({ state }) {
            Grid.loadGridsToDom((payload) => {
                state.grids = payload;
            });
        },
        setCopyValue({ state }, { copyValue }) {
            state.copyValue = copyValue
        },
        selectNextSibling({ commit, dispatch, state }, payload = { isMulti: true }) {
            if (state.selectedNodes.length) {
                let next = state.selectedNodes[state.selectedNodes.length - 1].nextSibling;
                while (next && next.isSelected()) {
                    next = next.nextSibling;
                }
                if (next) {
                    if (payload.isMulti) {
                        commit('setSelectedNode', { isMulti: true, node: next });
                    } else {
                        dispatch('selectNode', { isMulti: false, node: next });
                    }
                }
            }
        },
        selectPreviousSibling({ commit, dispatch, state }, payload = { isMulti: true }) {
            if (state.selectedNodes.length) {
                let prev = state.selectedNodes[state.selectedNodes.length - 1].previousSibling;
                while (prev && prev.isSelected()) {
                    prev = prev.previousSibling;
                }
                if (prev) {
                    if (payload.isMulti) {
                        commit('setSelectedNode', { isMulti: true, node: prev });
                    } else {
                        dispatch('selectNode', { isMulti: false, node: prev });
                    }
                }
            }
        },


    },
    modules: {
    }
})
